/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/** @jsx jsx */
import { jsx } from 'theme-ui';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { ChangeEvent, Fragment, memo, useState } from 'react';
import {
  Textarea,
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
  Input,
  NumberInput,
  NumberInputField,
  Text,
  Box,
  Select,
  Checkbox,
  useDisclosure,
  Flex,
  Tooltip,
  FormLabel,
  FormControl,
  ButtonGroup,
  IconButton,
  FormErrorMessage,
} from '@chakra-ui/core';
import isUndefined from 'lodash/isUndefined';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import { AnimatePresence, motion } from 'framer-motion';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import isNaN from 'lodash/isNaN';
import isObject from 'lodash/isObject';
import { useStoreActions, useStoreState } from '../../../../../models/hooks';
import { useSeekerContainerContext } from '../../SeekerContainerContext';
import { confirmationModalHeaderCSS } from './ConfirmationModal.styles';
import { ProposedSalary } from '../../../../../firebase/firestore/documents/position';
import { getTranslatedNumber, getTranslatedStringValue } from '../../../../../utils/localizedNumber';
import { SelectRequisition } from '../SelectRequisition';
import { BusinessSelectType, PositionSelectType } from '../../../../administration/positionManagement/PositionType';
import { useSelectedEngagedCandidate } from '../../../MessagingViewContext';
import { ComplianceContext, PositionComplianceReminder } from '../../../../../app/components/ComplianceReminder';
import { AdditionalNoteEditor } from '../../../../../app/components/AdditionalNoteEditor';
import usePosition from '../../../sidebar/seekerList/usePosition';
import { sanitizeOfferConditions } from '../../../../../utils/uiUtils';
import { clearErrorMessage, FormattedError, getErrorMessage, hasErrorMessage } from '../../../../../utils/FormErrorUtils';
import { DateAndTimePickerCoustomCSS } from '../../../../administration/positionManagement/createJobs/CreateJobPositionStyle';
import { ReactComponent as _IconCalendar } from '../../../../../assets/img/icon-calendar-blue.svg';
import { reactDatePickerCSS } from '../../../../calendar/scheduleSidebar/ReactDatePicker.Style';
import { drawerCSS } from '../../../../calendar/CalendarView.Style';
import { LanguageMap } from '../../../../../firebase/firestore/documents/user';
import { OfferConditionsType } from '../../../../../firebase/firestore/documents/candidateAudit';
import { Collections } from '../../../../../firebase/firestore/collections';
import { useDocumentSnapshot } from '../../../../../firebase/firestore/hooks/useDocumentSnapshot';
import { isSalaryFixed, validateSalary } from '../../../../../utils/salaryUtils';
import { Business } from '../../../../../firebase/firestore/documents/business';
import { ShowSalaryRange } from '../../../../../app/components/ShowSalaryRange';
import { EngagedCandidate } from '../../../../../firebase/firestore/documents/candidate';

const IconCalendar = memo(_IconCalendar);

export type HireConfirmationModalProps = {
  isOpen: boolean;
  disabled: boolean;
  positionSalaryDetails: ProposedSalary | undefined;
  onClose: () => void;
  requisitionEnabled?: boolean;
  candidateFromProps?: EngagedCandidate;
  onSuccessCallback?: () => void;
};

export const HireConfirmationModal = ({
  positionSalaryDetails,
  isOpen,
  disabled,
  onClose,
  requisitionEnabled,
  candidateFromProps,
  onSuccessCallback,
}: HireConfirmationModalProps): JSX.Element => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { selectedCandidate: selectedCandidateFromContext } = useSeekerContainerContext();
  const selectedEngagedCandidate = useSelectedEngagedCandidate();
  const selectedCandidate = candidateFromProps || selectedCandidateFromContext || selectedEngagedCandidate;

  const [noteContent, setNoteContent] = useState<string>('');
  const [salary, setSalary] = useState<string>('0');
  const [salaryError, setSalaryError] = useState<string>('');
  const [unit, setUnit] = useState<string>('hourly');
  const [customUnit, setCustomUnit] = useState<string>('');
  const [disabledButton, setButtonDisabled] = useState<boolean>(true);
  const [sendMessageToCandidates, setSendMessageToCandidates] = useState<boolean>(true);
  const [autoDismiss, setAutoDismiss] = useState<boolean>(true);
  const [updatedPositionId, setUpdatedPositionId] = useState<string | undefined>(selectedCandidate?.newHireData?.positionId);
  const [updatedBusinessId, setUpdatedBusinessId] = useState<string | undefined>(selectedCandidate?.newHireData?.businessId);
  const { isOpen: isOpenConformationModal, onOpen: onOpenConformationModal, onClose: onCloseConformationModal } = useDisclosure();

  const [additionalNote, setAdditionalNote] = useState<OfferConditionsType | undefined>('');
  const [isEnglishOfferEmpty, setIsEnglishOfferEmpty] = useState<boolean>(false);

  /* istanbul ignore next */
  const offerComplianceCheckedDate = useStoreState((s) => s.app.user?.offerComplianceCheckedDate);
  const [isSaveClicked, setIsSaveClicked] = useState<boolean>(false);
  const [hasUserAcknowledgedCompliance, setHasUserAcknowledgedCompliance] = useState<boolean>(false);

  const positionData = usePosition(updatedPositionId || selectedCandidate?.position);
  /* istanbul ignore next */
  const isOfferConditionsEnabled = useStoreState((s) => s.app.accounts?.configuration?.offerConditionsEnabled);

  /* istanbul ignore next */
  const saveOfferMadeNote = useStoreActions((actions) => actions.account.saveOfferMadeNote);

  const MAXIMUM_SALARY = positionSalaryDetails?.maxValue ? Number(positionSalaryDetails.maxValue) / 100 : 0;

  const MINIMUM_SALARY = positionSalaryDetails?.minValue ? Number(positionSalaryDetails.minValue) / 100 : 0;

  const [positionMinSalary, setPositionMinSalary] = useState(MINIMUM_SALARY);

  const [positionMaxSalary, setPositionMaxSalary] = useState(MAXIMUM_SALARY);

  const [formErrors, setFormErrors] = useState<FormattedError[]>([]);

  const [jobJoinDate, setJobJoinDate] = useState<Date | null | undefined>(undefined);

  const business: Business | undefined = useDocumentSnapshot<Business>(Collections.BUSINESSES, selectedCandidate?.business);

  const isCountryAustralia = business?.country === 'Australia';

  React.useEffect(() => {
    return () => {
      setIsLoading(false);
      setNoteContent('');
      setSalary('0');
      setUnit('hourly');
      setCustomUnit('');
      setButtonDisabled(true);
    };
  }, []);

  React.useEffect(() => {
    setSalary(
      positionSalaryDetails?.minValue && (positionSalaryDetails?.unit || positionSalaryDetails?.customUnit)
        ? (getTranslatedNumber(Number((positionSalaryDetails?.minValue as any) / 100).toFixed(2), i18n.language) as any)
        : '',
    );
    setUnit(positionSalaryDetails?.unit ?? 'hourly');
    setCustomUnit(positionSalaryDetails?.customUnit ?? '');
    setNoteContent(
      isCountryAustralia
        ? ''
        : positionSalaryDetails?.minValue && (positionSalaryDetails?.unit || positionSalaryDetails?.customUnit)
        ? `${t('messaging:actions.hire.confirmationModal.text1')}$${getTranslatedNumber(
            (positionSalaryDetails?.minValue as any) / 100,
            i18n.language,
          )} (${t(
            `messaging:actions.hire.confirmationModal.${positionSalaryDetails?.unit ?? positionSalaryDetails?.customUnit}`,
          )})`
        : '',
    );
  }, [positionSalaryDetails, i18n.language, t, isCountryAustralia]);

  // istanbul ignore next
  React.useEffect(() => {
    if (positionData?.offerConditions) {
      setAdditionalNote(positionData?.offerConditions);
    } else {
      setAdditionalNote(null);
    }
  }, [positionData]);

  React.useEffect(() => {
    if (!isCountryAustralia && (noteContent.replace(/<(.|\n)*?>/g, '').trim().length === 0 || unit === '')) {
      setButtonDisabled(false);
    } else if (!/^(?:\d*[.|,]\d{1,2}|\d+)$/.exec(salary) || salary === '' || (salary as any) <= -1) {
      if (salary === '' && isCountryAustralia) {
        setButtonDisabled(false);
      } else {
        setButtonDisabled(true);
      }
    } else if (unit === 'custom' && customUnit === '') {
      setButtonDisabled(true);
    } else {
      setButtonDisabled(false);
    }
  }, [noteContent, unit, salary, customUnit, isCountryAustralia]);

  const checkLengthOfStringSeparatedByDotOrComma = (givenString: string, stringLength: number) => {
    let allowInput = true;

    const spliterator = givenString.includes('.') ? givenString.split('.') : givenString.split(',');
    if (spliterator[1]) {
      if (spliterator[1].length > stringLength) {
        allowInput = false;
      }
    }

    return allowInput;
  };
  const updateNoteContent = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setNoteContent(e.target.value);
  };

  const handleCustomUnitChange = (e: ChangeEvent<HTMLInputElement>) => {
    setCustomUnit(e.target.value);
  };

  const handleSalaryChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (!checkLengthOfStringSeparatedByDotOrComma(value, 2)) return;

    const { validatedSalary, error } = validateSalary(value, positionMinSalary, positionMaxSalary, isCountryAustralia, t);
    setSalary(validatedSalary);
    setSalaryError(error);
  };

  const handleUnitChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setUnit(e.target.value);
  };

  // istanbul ignore next
  const handleAdditionalNote = (note: LanguageMap<string> | undefined) => {
    setAdditionalNote(note);
  };

  const submitNote = async () => {
    onCloseConformationModal();
    if (!selectedCandidate) return;
    if (!requisitionEnabled && isUndefined(jobJoinDate) && !isCountryAustralia) {
      setFormErrors([
        { label: 'startingDate', message: t('messaging:actions.hire.confirmationModal.employmentStartDateRequired') },
      ]);
      return;
    }
    let updatedSalary: string;
    if (salary.includes(',')) {
      updatedSalary = salary.toString().replace(',', '.');
    } else {
      updatedSalary = salary.toString();
    }
    const isSalaryEmpty = isEmpty(updatedSalary);
    try {
      setIsLoading(true);
      await saveOfferMadeNote({
        positionId: selectedCandidate.position,
        seekerId: selectedCandidate.seeker,
        offerMade: noteContent,
        salary: isSalaryEmpty
          ? // istanbul ignore next
            null
          : updatedSalary,
        salaryUnit: isSalaryEmpty
          ? // istanbul ignore next
            null
          : unit,
        customSalaryUnit: isSalaryEmpty
          ? // istanbul ignore next
            null
          : customUnit,
        sendMessageToCandidates,
        updatedPositionId,
        updatedBusinessId,
        autoDismiss,
        offerConditions: /* istanbul ignore next */ sanitizeOfferConditions(additionalNote as LanguageMap<string>),
        ...(jobJoinDate
          ? // istanbul ignore next
            { employmentStartDate: jobJoinDate }
          : {}),
      });
      onSuccessCallback?.();
      onClose();
    } finally {
      setIsLoading(false);
      toast({
        title: t('notes:offerSuccessMsg.title'),
        description: t('notes:offerSuccessMsg.description'),
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // istanbul ignore next
  const handleJobJoinDateChange = (date: Date) => {
    setJobJoinDate(date);
  };

  const handleConfirmButtonClick = async () => {
    if (
      additionalNote &&
      isObject(additionalNote) &&
      isEmpty(additionalNote?.en) &&
      (!isEmpty(additionalNote?.fr) || !isEmpty(additionalNote?.es))
    ) {
      setIsEnglishOfferEmpty(true);
      return;
    }

    setIsSaveClicked(true);
    /* istanbul ignore next */
    if (hasUserAcknowledgedCompliance || !isNil(offerComplianceCheckedDate)) {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      updatedBusinessId || updatedPositionId ? onOpenConformationModal() : await submitNote();
    }
  };

  const CustomDatePickerInput = (props: React.HTMLProps<HTMLInputElement>, ref: React.Ref<HTMLInputElement>) => (
    <ButtonGroup className="customDatepicker" size="md" isAttached variant="outline" onClick={props.onClick} ref={ref}>
      <Button mr="-px" style={{ width: '100%' }}>
        {props.value}
      </Button>
      <IconButton style={{ padding: '0.2rem' }} aria-label="calendar" icon={<IconCalendar width={20} height={20} />} />
    </ButtonGroup>
  );

  const isConfirmBtnDisabled =
    disabledButton || disabled || isLoading || !!salaryError || (updatedBusinessId ? !updatedPositionId : false);

  const validateAndSetSalary = (givenMinSalary?: number, givenMaxSalary?: number) => {
    const currentMinSalary = givenMinSalary ?? /* istanbul ignore next */ positionMinSalary;
    const currentMaxSalary = givenMaxSalary ?? /* istanbul ignore next */ positionMaxSalary;
    setSalaryError('');
    if (isSalaryFixed(currentMinSalary, currentMaxSalary)) {
      setSalary(String(currentMinSalary));
      return;
    }
    const { validatedSalary, error } = validateSalary(salary, currentMinSalary, currentMaxSalary, isCountryAustralia, t);
    setSalary(validatedSalary);
    setSalaryError(error);
  };

  const onHandleJobChange = (jobPositionItem: PositionSelectType | null) => {
    setUpdatedPositionId(jobPositionItem?.id);
    const UPPDATED_POSITION_MIN_SALARY = jobPositionItem?.proposedSalary?.minValue
      ? Number(jobPositionItem?.proposedSalary?.minValue) / 100
      : 0;
    const UPDATED_POSITION_MAX_SALARY = jobPositionItem?.proposedSalary?.maxValue
      ? Number(jobPositionItem?.proposedSalary?.maxValue) / 100
      : 0;

    setPositionMinSalary(UPPDATED_POSITION_MIN_SALARY);
    setPositionMaxSalary(UPDATED_POSITION_MAX_SALARY);
    setUnit(jobPositionItem?.proposedSalary?.unit ?? 'hourly');
    setCustomUnit(jobPositionItem?.proposedSalary?.customUnit ?? '');
    validateAndSetSalary(UPPDATED_POSITION_MIN_SALARY, UPDATED_POSITION_MAX_SALARY);
  };

  const isCurrentSalaryFixedInAustralia = isSalaryFixed(positionMinSalary, positionMaxSalary) && isCountryAustralia;
  const hasValidSalaries = !isNaN(positionMaxSalary) && !isNaN(positionMinSalary);
  const invalidRange = positionMaxSalary <= positionMinSalary || positionMaxSalary === 0;

  const isSalaryDisabled = isCurrentSalaryFixedInAustralia ? false : hasValidSalaries && invalidRange;

  return (
    <Modal
      id="HireConfirmationModal"
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      closeOnOverlayClick={false}
      scrollBehavior="inside"
      size="4xl"
    >
      <ModalOverlay>
        <ModalContent>
          <ModalHeader css={confirmationModalHeaderCSS} className="modal-header" style={{ color: 'black' }}>
            {t('messaging:actions.hire.confirmationModal.header')}
          </ModalHeader>
          <ModalBody css={[drawerCSS, reactDatePickerCSS]}>
            <AnimatePresence>
              {isNil(offerComplianceCheckedDate) && !hasUserAcknowledgedCompliance && (
                <motion.div
                  initial={{ opacity: 1, scale: 1 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{
                    opacity: 0,
                    scale: 0.95,
                    transition: { duration: 0.3, ease: 'easeInOut' },
                  }}
                >
                  <PositionComplianceReminder
                    isOpen={false}
                    onClose={() => ''}
                    context={ComplianceContext.OFFER}
                    onConfirm={() => setHasUserAcknowledgedCompliance(true)}
                    userDidSomeAction={isSaveClicked}
                  />
                </motion.div>
              )}
            </AnimatePresence>
            <SelectRequisition
              positionId={selectedCandidate?.position || ''}
              onRequisitionChanged={() => {}}
              handleJobLocationChange={(businessLocationItem: BusinessSelectType | null) => {
                setUpdatedBusinessId(businessLocationItem?.id);
                setUpdatedPositionId(undefined);
              }}
              handleJobChange={onHandleJobChange}
              isOpenConformationModal={isOpenConformationModal}
              onHandleConformationModal={async (conformation: boolean) => {
                if (conformation) {
                  await submitNote();
                } else {
                  onCloseConformationModal();
                }
              }}
              candidateFromProps={candidateFromProps}
            />
            <Box display="flex" mt="26px" mr="16px">
              <Text mb="8px" maxWidth="90px">{` ${t('messaging:actions.hire.confirmationModal.salary')} ($) : `}</Text>
              {!isCountryAustralia && (
                <Box as="span" color="red.500" ml={1}>
                  *
                </Box>
              )}
              <Box display="flex" justifyContent="space-between" style={{ marginTop: '-6px', marginLeft: '12px' }}>
                <Select
                  onChange={(e: ChangeEvent<HTMLSelectElement>) => handleUnitChange(e)}
                  data-testid="UnitValue"
                  size="sm"
                  style={{ height: '4.8vh', width: '150px' }}
                  value={unit}
                  disabled={Boolean(positionSalaryDetails?.unit)}
                >
                  <option value="hourly">{t('messaging:actions.hire.confirmationModal.hourly')}</option>
                  <option value="annual">{t('messaging:actions.hire.confirmationModal.annually')}</option>
                  <option value="custom">{t('messaging:actions.hire.confirmationModal.custom')}</option>
                </Select>
                {unit === 'custom' ? (
                  <Input
                    style={{ marginLeft: '15px', height: '4.8vh', width: '150px' }}
                    value={customUnit}
                    data-testid="CustomUnitValue"
                    onChange={(e: ChangeEvent<HTMLInputElement>) => handleCustomUnitChange(e)}
                    placeholder={t('messaging:actions.hire.confirmationModal.customUnit')}
                    size="sm"
                    mb="1.3rem"
                    disabled={Boolean(positionSalaryDetails?.unit)}
                  />
                ) : (
                  ''
                )}

                <FormControl
                  isInvalid={!!salaryError}
                  mb="1.3rem"
                  style={{ marginLeft: '15px' }}
                  display="flex"
                  flexDirection="column"
                  alignItems="start"
                  justifyItems="center"
                >
                  <Flex alignItems="center">
                    <NumberInput
                      max={
                        // istanbul ignore next
                        (i18n.language === 'en' && positionMaxSalary) || undefined
                      }
                      precision={2}
                      value={getTranslatedStringValue(salary, i18n.language)}
                      isDisabled={isSalaryDisabled}
                    >
                      <NumberInputField
                        _disabled={{ opacity: 0.8 }}
                        style={{ height: '4.8vh', width: '150px' }}
                        data-testid="SalaryValue"
                        type="text"
                        value={getTranslatedStringValue(salary, i18n.language)}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleSalaryChange(e)}
                        placeholder={t('messaging:actions.hire.confirmationModal.salaryValue')}
                        isDisabled={isSalaryDisabled}
                        // onBlur={() => validateAndSetSalary()}
                      />
                    </NumberInput>
                    <Text pl="6px" mt="4px" fontSize={14}>
                      <ShowSalaryRange
                        minSalary={positionMinSalary}
                        maxSalary={positionMaxSalary}
                        unit={unit}
                        customUnit={customUnit}
                      />
                    </Text>
                  </Flex>
                  <FormErrorMessage>{salaryError}</FormErrorMessage>
                </FormControl>
              </Box>
            </Box>

            <Box w="49%" css={DateAndTimePickerCoustomCSS} id="elementID_earliest_start_date" display="flex" mt="1rem" mr="16px">
              <FormControl isInvalid={hasErrorMessage(formErrors, 'startingDate')} isRequired={!isCountryAustralia}>
                <FormLabel mb="8px" fontWeight="bold">
                  {t('messaging:actions.hire.employmentStartDate')}
                </FormLabel>
                <DatePicker
                  selected={jobJoinDate}
                  customInput={React.createElement(React.forwardRef(CustomDatePickerInput))}
                  locale={i18n.language}
                  dateFormat="P"
                  shouldCloseOnSelect
                  minDate={new Date()}
                  onChange={
                    /* istanbul ignore next */ (date) => {
                      handleJobJoinDateChange(date as Date);
                      setFormErrors(clearErrorMessage(formErrors, 'startingDate'));
                    }
                  }
                  data-testid="dateOfJoining"
                />
                <FormErrorMessage>{getErrorMessage(formErrors, 'startingDate')}</FormErrorMessage>
              </FormControl>
            </Box>

            <FormControl isInvalid={isEnglishOfferEmpty} isRequired>
              {isOfferConditionsEnabled && (
                /* istanbul ignore next */
                <AdditionalNoteEditor
                  additionalNote={additionalNote}
                  handleNote={handleAdditionalNote}
                  showToolbar
                  isReadOnly={false}
                />
              )}
              <FormErrorMessage>{t('jobPosition:offerConditionRequired')}</FormErrorMessage>
            </FormControl>
            <Box mb={3}>
              <Text fontSize="14px" fontWeight={700} mt="12px">
                {t('requisition:offerNotes')}
              </Text>
              <Textarea
                value={noteContent}
                data-testid="NoteContent"
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => updateNoteContent(e)}
                focusBorderColor="gray"
                style={{ height: '10vh' }}
                placeholder={t('messaging:actions.hire.confirmationModal.body')}
              />
            </Box>

            <Flex mt={6}>
              <Tooltip label={t('messaging:actions.hire.sendMessageToCandidatesToolTip')} shouldWrapChildren placement="top">
                <Checkbox
                  mr={6}
                  isChecked={sendMessageToCandidates}
                  isDisabled={!!(updatedBusinessId || updatedPositionId)}
                  data-testid="sendMessageToCandidates"
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setSendMessageToCandidates(e.target.checked)}
                >
                  {t('messaging:actions.hire.sendMessageToCandidates')}
                </Checkbox>
              </Tooltip>
              <Tooltip label={t('messaging:actions.hire.autoDismissCandidatesToolTip')} shouldWrapChildren placement="top">
                <Checkbox
                  mr={2}
                  isChecked={autoDismiss}
                  isDisabled={!sendMessageToCandidates}
                  data-testid="autoDismissCandidates"
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setAutoDismiss(e.target.checked)}
                >
                  {t('messaging:actions.hire.autoDismissCandidates')}
                </Checkbox>
              </Tooltip>
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button data-testid="CancelButton" colorScheme="red" mr={3} onClick={onClose} disabled={isLoading}>
              {t('messaging:actions.hire.confirmationModal.cancelButton')}
            </Button>
            <Button
              data-testid="ConfirmButton"
              colorScheme="blue"
              onClick={async () => {
                await handleConfirmButtonClick();
              }}
              isLoading={isLoading}
              disabled={isConfirmBtnDisabled}
            >
              {t('messaging:actions.hire.confirmationModal.saveButton')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </ModalOverlay>
    </Modal>
  );
};
